import { takeEvery, call, race, take, delay, put, select, getContext } from 'redux-saga/effects';
import { buffers, channel } from 'redux-saga';
import { getLogger } from '../../logger/index';
import { sendCancelCall, sendAcceptCall } from '../communication/actions';
import videoSelectors from './selectors';
import {
  startCallByRemote,
  callFinishActions,
  callFinishByRemoteActions,
  sendViewerSettingsByRemote,
  acceptCall,
  setLocalStream,
  setRemoteStream,
  cancelCall,
  newCustomerCall,
  endCall,
  acceptNewCustomerCall,
  setStatus,
} from './actions';
import authSelectors from '../auth/selectors';
import { waitForAuth } from '../auth/sagaUtils';
import { contact as contactEntity } from '../communication/entities';
import { createSlaveConnectionChannel, createMasterConnectionChannel } from './kvsWebRTC';
import { fetchAndPropagateCustomerKVSSettingsSaga } from './kvsSettings';
import { watchOutgoingConnectionEvents } from './sagaOutgoingCall';
import { deactivateCustomerVideoCallSaga } from './sagaCustomer';
import chatActions from '../chat/actions';
import appSelectors from '../app/selectors';

const logger = getLogger('communicationVideo :: incommingCall');

export const incomingCallChannel = channel(buffers.sliding(1));

function* incomingCustomerCallSaga({ username, name, type, isAnonymousUser, customerVideoCall, role }) {
  let connectionChannel;
  try {
    yield waitForAuth();
    const authId = yield select(authSelectors.getId);
    const authName = yield select(authSelectors.getName);

    logger.debug('wait for answare');
    yield incomingCallChannel.put('ready');

    const isAccepted = yield call(waitForAnsware, type, {
      from: username,
      fromName: name,
      to: authId,
      toName: authName,
    });
    if (!isAccepted) {
      logger.debug('Stop saga because the call was not accepted');
      return;
    }
    logger.debug('Trigger acceptNewCustomerCall action', arguments[0], arguments[0].uuid);
    yield put(acceptNewCustomerCall(arguments[0], arguments[0].uuid));

    logger.debug('send accept call to caller');
    yield put(
      sendAcceptCall(type === 'video', {
        from: username,
        fromName: name,
        to: authId,
        toName: authName,
      })
    );

    yield put(setStatus({ source: 'viewer', info: 'preparing' }));
    logger.debug('Fetch KinesisVideoStream settings');
    const kvsSettings = yield call(fetchAndPropagateCustomerKVSSettingsSaga, {
      username,
      name,
      type,
      isAnonymousUser,
      customerVideoCall,
    });
    if (!kvsSettings) {
      logger.error('Failed while fetching KVS settings');
      yield put(endCall);
    }
    // settings for master connection
    const settings = {
      ...kvsSettings,
      channelEndpoints: kvsSettings.masterChannelEndpoints,
      iceServers: kvsSettings.masterIceServers,
    };

    logger.debug('Create WebRTC connection');
    const mediaDevices = yield getContext('mediaDevices');
    const isAppActive = yield select(appSelectors.isActive);
    const RTCPeerConnection = yield getContext('RTCPeerConnection');
    connectionChannel = createMasterConnectionChannel({
      settings,
      RTCPeerConnection,
      type: isAppActive ? type : 'audio',
      mediaDevices,
    });

    logger.debug('watch connection events');

    yield call(watchOutgoingConnectionEvents, connectionChannel, {
      id: username,
      name,
      role,
      isAnonymousUser,
      customerVideoCall,
    });
  } catch (e) {
    logger.error(e);
  } finally {
    logger.debug('incomingCustomerCallSaga closed');
    if (connectionChannel) {
      connectionChannel.close();
    }

    logger.debug('Deactivate customomer video call', customerVideoCall);
    yield call(deactivateCustomerVideoCallSaga, customerVideoCall);
    logger.debug('Close customer chat', username);
    yield put(chatActions.closeCustomerChat(username));
    logger.debug('Remove customer from the contact list', username);
    yield put(contactEntity.removeListItem(username));
  }
}
function* incomingCallSaga({ from, type }) {
  let connectionChannel;
  try {
    yield waitForAuth();
    const authId = yield select(authSelectors.getId);
    const authName = yield select(authSelectors.getName);

    logger.debug('wait for answare');
    yield incomingCallChannel.put('ready');

    const isAccepted = yield call(waitForAnsware, type, {
      from: from.id,
      fromName: from.name,
      to: authId,
      toName: authName,
    });
    if (!isAccepted) {
      logger.debug('Stop saga because the call was not accepted');
      return;
    }

    logger.debug('send accept call to caller');
    yield put(
      sendAcceptCall(type === 'video', {
        from: from.id,
        fromName: from.name,
        to: authId,
        toName: authName,
      })
    );

    logger.debug('wait for settings');
    const {
      payload: { settings },
    } = yield take(sendViewerSettingsByRemote);

    logger.debug('Create WebRTC connection');
    const mediaDevices = yield getContext('mediaDevices');
    const isAppActive = yield select(appSelectors.isActive);
    const RTCPeerConnection = yield getContext('RTCPeerConnection');
    connectionChannel = createSlaveConnectionChannel({
      settings,
      RTCPeerConnection,
      type: isAppActive ? type : 'audio',
      mediaDevices,
    });

    logger.debug('watch connection events');
    yield call(watchConnectionEvents, connectionChannel, from);
  } catch (e) {
    logger.error(e);
  } finally {
    logger.debug('incomingCallSaga closed');
    if (connectionChannel) {
      connectionChannel.close();
    }
  }
}

function* waitForAnsware(type, socketArg) {
  try {
    // wait max 30s and than end the call or stop on cancel
    const { timeout, rejected, cancelledByRemote } = yield race({
      callAccepted: take(acceptCall),
      rejected: take(callFinishActions),
      cancelledByRemote: take(callFinishByRemoteActions),
      timeout: delay(40000, true),
    });
    if (rejected) {
      logger.debug('Reject call');
      return false;
    }
    if (cancelledByRemote) {
      logger.debug('Cancel call by remote before answare');
      return false;
    }
    if (timeout) {
      logger.debug('Call not answared before timeout - send cancel call');
      yield put(sendCancelCall(type === 'video', socketArg));
      const remote = yield select(videoSelectors.getRemoteCall(socketArg.from));
      yield put(cancelCall(remote.uuid));
      return false;
    }
  } catch (e) {
    logger.error(e);
  }
  return true;
}
function* watchConnectionEvents(connectionChannel, from) {
  try {
    while (true) {
      const { eventName, args } = yield take(connectionChannel);

      const actionMap = {
        remoteStream: function*() {
          logger.debug('Set remote stream');
          yield put(setRemoteStream(args, from));
        },
        localStream: function*() {
          logger.debug('Set local stream');
          yield put(setLocalStream(args));
        },
        statusUpdate: function*() {
          yield put(setStatus(args));
        },
      };

      if (actionMap[eventName]) {
        yield call(actionMap[eventName]);
      }
    }
  } catch (e) {
    logger.error(e);
  } finally {
    yield put(setStatus({ source: 'presenter', info: 'disconnected' }));
  }
}

function* startCallByRemoteSaga({ payload: { from, type } }) {
  yield put(setStatus({ source: 'viewer', info: 'calling' }));
  yield race({
    watch: call(incomingCallSaga, { from, type }),
    cancel: take([...callFinishActions, ...callFinishByRemoteActions]),
  });

  logger.debug('finish watching incomming call events on end call');
}

function* startCallByCustomerSaga({ payload }) {
  yield race({
    watch: call(incomingCustomerCallSaga, payload),
    cancel: take([...callFinishActions, ...callFinishByRemoteActions]),
  });

  logger.debug('finish watching incomming call events on end call');
}

function* watchIncommingCallActions() {
  try {
    logger.debug('Watch incomming call actions');
    yield takeEvery(startCallByRemote, startCallByRemoteSaga);

    logger.debug('Watch incomming call from customer actions');
    yield takeEvery(newCustomerCall, startCallByCustomerSaga);
  } catch (e) {
    logger.error(e);
  }
}

export default watchIncommingCallActions;
